import React, { useState, useEffect, useContext } from 'react';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Household as GdsHousehold, Person } from '../../gds-api-client';
import {
  FormProps,
  BulkFormProps,
  ModalType,
  Invitation,
  GuestModalProps,
  Household,
  GuestWeddingQuestions,
  FocusOn,
  Event,
} from '../interfaces';
import ComponentStateContext from '../ComponentStateContext';
import {
  createEmptyLeader,
  denormalizeData,
  denormalizeBulkData,
  removePeopleNulls,
  getRsvpNoteAnswer,
} from '../helpers';
import { GdsClientContext } from '../GdsClient';
import RoutesWithFormik from './RoutesWithFormik';

export const emptyAddress = {
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  fullAddress: '',
};

const extractInvitationsToShow = (people: Person[]): Invitation[] => {
  let invitations: Invitation[] = [];
  people.forEach(person => {
    if (person.invitations) {
      invitations = person.invitations?.reduce((obj, invite) => {
        const newObj = { ...obj };
        if (person.isLeader || !obj[invite.eventId as any]) {
          newObj[invite.eventId as any] = invite;
        }
        return newObj;
      }, invitations);
    }
  });
  return Object.values(invitations);
};

const determineInitialValue = (
  household: Household | undefined,
  events: any[],
  selectedEvent: Event | undefined,
  guestWeddingQuestions: GuestWeddingQuestions
): FormProps => {
  if (!household) {
    return {
      description: '',
      rsvpNote: '',
      coupleNote: '',
      address: emptyAddress,
      people: [createEmptyLeader(events, selectedEvent)],
      invitations: [],
      answers: [],
    };
  }

  const description = household.description ? household.description : '';
  const coupleNote = household.coupleNote ? household.coupleNote : '';
  const address = household.address ? household.address : emptyAddress;
  const uncleanedPeople = household.people ? household.people : [createEmptyLeader(events, selectedEvent)];
  const people = removePeopleNulls(uncleanedPeople);
  const invitations = extractInvitationsToShow(people as Person[]);
  const rsvpNote = getRsvpNoteAnswer(guestWeddingQuestions, household);
  const answers = household?.answers || [];

  return {
    description,
    rsvpNote: rsvpNote as string | null,
    coupleNote,
    address: {
      street1: address.street1 || '',
      street2: address.street2 || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
      country: address.country || '',
      fullAddress: address.fullAddress || '',
    },
    people,
    invitations,
    answers,
  };
};

const setBulkInitialValue = (
  households: Household[] | undefined,
  events: any[],
  selectedEvent: Event | undefined
): BulkFormProps => {
  if (!households?.length) {
    return [
      {
        description: '',
        rsvpNote: '',
        coupleNote: '',
        address: emptyAddress,
        people: [createEmptyLeader(events, selectedEvent)],
        answers: [],
      },
    ];
  }

  const initialValues = households?.map(household => {
    const uncleanedPeople = household.people ? household.people : [createEmptyLeader(events, selectedEvent)];
    const people = removePeopleNulls(uncleanedPeople);

    return {
      ...household,
      people,
    };
  });

  return initialValues;
};

const validationSchema = Yup.object().shape({
  people: Yup.array().of(
    Yup.object().shape({
      lastName: Yup.string().test({
        message: 'Please enter a first or last name',
        test(value): boolean {
          const { firstName } = this.parent;
          if (!firstName) return value !== '' && value !== undefined;
          return true;
        },
      }),
      firstName: Yup.string().test({
        message: 'Please enter a first or last name',
        test(value): boolean {
          const { lastName } = this.parent;
          if (!lastName) return value !== '' && value !== undefined;
          return true;
        },
      }),
      phone: Yup.string().when('isLeader', {
        is: true,
        then: Yup.string().test({
          test(number): boolean {
            if (!number) return true;
            const internationalPattern = /^\+.*$/;
            const usPattern = /^(?:(?:(?!0|1)\d{3}-|\((?!0|1)\d{3}\)\s?)\d{3}-\d{4}|(?!0|1)\d{10})$/;
            if (internationalPattern.test(number)) {
              return internationalPattern.test(number);
            }
            return usPattern.test(number);
          },
          message: 'Make sure this phone number is correct!',
        }),
      }),
      email: Yup.string().when('isLeader', {
        is: true,
        then: Yup.string().email('Make sure this email is correct!'),
      }),
    })
  ),
});

const FormikForm: React.FC<GuestModalProps> = props => {
  const {
    guestWeddingQuestions,
    household,
    householdsList,
    events,
    wedding,
    selectedEvent,
    onClose,
    onSuccess,
    onBulkSuccess,
    showError,
    clearError,
    contactFocus,
    topLabel,
  } = props;

  const [initialValues, setInitialValues] = useState(() =>
    householdsList?.length
      ? setBulkInitialValue(householdsList, events, selectedEvent)
      : determineInitialValue(household, events, selectedEvent, guestWeddingQuestions)
  );

  const getFocus = (focus: string | undefined): FocusOn => {
    if (focus === 'email') {
      return FocusOn.email;
    }
    if (focus === 'phone') {
      return FocusOn.phone;
    }
    return FocusOn.all;
  };

  const [shouldClose, setShouldClose] = useState(false);
  const { setGuestsDeleted } = useContext(ComponentStateContext);
  const type = household ? ModalType.edit : ModalType.add;
  const focusOn = getFocus(contactFocus);

  const gdsClient = useContext(GdsClientContext);
  const handleClose = (): void => {
    onClose();
    setGuestsDeleted(0);
  };

  const reSetInitialValues = React.useCallback(() => {
    const value = householdsList?.length
      ? setBulkInitialValue(householdsList, events, selectedEvent)
      : determineInitialValue(household, events, selectedEvent, guestWeddingQuestions);
    setInitialValues(value);
  }, [events, guestWeddingQuestions, household, householdsList, selectedEvent]);

  useEffect(() => {
    reSetInitialValues();
  }, [reSetInitialValues]);

  const onSubmit = async (data: any, { resetForm }: any): Promise<void> => {
    const formData = householdsList?.length ? denormalizeBulkData(data) : denormalizeData(data);

    try {
      let response: any;
      if (householdsList?.length) {
        const householdsToUpdate = formData as GdsHousehold[];
        const households = householdsToUpdate.map(household => ({
          ...household,
          weddingId: wedding.id, // bulk update requires weddingId inside household object
        }));
        response = await gdsClient.upsertHouseholdBulk(households);
      } else if (type === ModalType.edit && household) {
        const householdToUpdate = { ...formData, id: household.id } as GdsHousehold;
        response = await gdsClient.updateHousehold(wedding.id, householdToUpdate);
      } else if (type === ModalType.add && !household) {
        response = await gdsClient.createHousehold(wedding.id, formData as Omit<GdsHousehold, 'id'>);
      }

      clearError();

      if (householdsList?.length && onBulkSuccess) {
        onBulkSuccess(response.data as Array<Household>);
      } else if (onSuccess) {
        onSuccess(response.data as Household);
      }

      // reset and clear form
      if (type === ModalType.add) resetForm();

      // reset value state for form values after submission
      // to prevent changes from one form presisting into the other
      if (type === ModalType.edit) {
        const { people, answers } = response.data;
        const resetFormValues = determineInitialValue(
          { ...household, people, answers } as Household,
          events,
          selectedEvent,
          guestWeddingQuestions
        );
        resetForm(resetFormValues);
        setInitialValues(resetFormValues);
      }

      // eslint-disable-next-line no-unused-expressions
      shouldClose && handleClose();
      setShouldClose(false);
    } catch (e) {
      console.error(e);
      showError();
    }
  };
  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
        isInitialValid
      >
        {(formProps: FormikProps<FormProps | BulkFormProps>): JSX.Element => (
          <RoutesWithFormik
            {...props}
            focusOn={focusOn}
            topLabel={topLabel}
            formProps={formProps}
            setShouldClose={setShouldClose}
          />
        )}
      </Formik>
    </>
  );
};

export default FormikForm;
